import { c as _c } from "react/compiler-runtime";
import { Fragment, useCallback, useMemo } from 'react';
import { Box, Spinner, Text } from '@chakra-ui/react';
import { BarChartState } from '@/components/pages/Digraph/VariableWithBarChart/VariableWithBarChart';
import ShimmerProgressBar from '@/components/util-component/ShimmerProgressBar';
import { VariableStateSelectEntry } from '@/services/db/schema';
import { VariableStateId } from '@/typeAlias';
import { Interval } from '@/types/variable';
import calculateColorOfState from '@/utils/network/calculateColorOfState';
import { intervalToString } from '@/utils/network/variableInterval/intervalToString';
type BarChartProps = {
  states: BarChartState[];
  assumption: number | undefined;
  toggleAssumption: (stateId: VariableStateId) => void;
  intervals: Interval[] | undefined;
  optimalStateId: string | null;
  variableStatesWithMetadata: Record<VariableStateId, VariableStateSelectEntry>;
};
const rowHeight = 18;
export const calculateBarChartHeight = (stateCount: number) => stateCount * rowHeight;
function BarChart(t0) {
  const $ = _c(27);
  const {
    states,
    assumption,
    toggleAssumption,
    intervals,
    optimalStateId,
    variableStatesWithMetadata
  } = t0;
  let t1;
  if ($[0] !== states || $[1] !== variableStatesWithMetadata) {
    let t2;
    if ($[3] !== variableStatesWithMetadata) {
      t2 = state => variableStatesWithMetadata?.[state.stateId]?.name;
      $[3] = variableStatesWithMetadata;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = states.map(t2);
    $[0] = states;
    $[1] = variableStatesWithMetadata;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const stateNames = t1;
  let t2;
  if ($[5] !== states) {
    t2 = states.map(_temp);
    $[5] = states;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const stateIds = t2;
  const handleStopPropagation = _temp2;
  let t3;
  if ($[7] !== states.length) {
    t3 = calculateBarChartHeight(states.length);
    $[7] = states.length;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = `${t3}px`;
  let t5;
  if ($[9] !== assumption || $[10] !== intervals || $[11] !== optimalStateId || $[12] !== stateIds || $[13] !== stateNames || $[14] !== states || $[15] !== toggleAssumption) {
    let t6;
    if ($[17] !== assumption || $[18] !== intervals || $[19] !== optimalStateId || $[20] !== stateIds || $[21] !== stateNames || $[22] !== toggleAssumption) {
      t6 = (state_0, stateIndex) => {
        const handleDoubleClick = () => toggleAssumption(state_0.stateId);
        const stateColor = calculateColorOfState(stateIds, optimalStateId, state_0.stateId);
        return <Fragment key={state_0.stateId}><Box columnGap={1} cursor="pointer" display="grid" gridTemplateColumns="minmax(0, 1fr) 52px 60px" maxHeight="27px" padding={0} width="100%">{stateNames[stateIndex] ? <Text aria-label={`${stateNames[stateIndex]} ${Math.round(state_0.probability * 100)}%`} fontSize="xs" fontWeight={assumption === stateIndex ? "bold" : "normal"} overflow="hidden" textOverflow="ellipsis" title={generateTitle(stateNames[stateIndex], intervals, stateIndex)} whiteSpace="nowrap" onDoubleClick={handleDoubleClick}>{stateNames[stateIndex]}</Text> : <Box alignItems="center" display="flex" justifyContent="center" width="100%"><Spinner size="xs" /></Box>}<Text fontSize="xs" fontWeight={assumption === stateIndex ? "bold" : "normal"} textAlign="right" title={(state_0.probability * 100).toFixed(4).toString().concat("%")} onDoubleClick={handleDoubleClick}>{formatStateProbability(state_0.probability)}</Text><Box marginTop="3px"><ShimmerProgressBar color={stateColor} height="3" percentage={state_0.probability * 100} onDoubleClick={handleDoubleClick} /></Box></Box></Fragment>;
      };
      $[17] = assumption;
      $[18] = intervals;
      $[19] = optimalStateId;
      $[20] = stateIds;
      $[21] = stateNames;
      $[22] = toggleAssumption;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    t5 = states.map(t6);
    $[9] = assumption;
    $[10] = intervals;
    $[11] = optimalStateId;
    $[12] = stateIds;
    $[13] = stateNames;
    $[14] = states;
    $[15] = toggleAssumption;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[24] !== t4 || $[25] !== t5) {
    t6 = <Box alignItems="center" display="grid" h={t4} rowGap="1px" title="" onDoubleClick={handleStopPropagation}>{t5}</Box>;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  return t6;
}
function _temp2(event) {
  event.stopPropagation();
}
function _temp(t0) {
  const {
    stateId
  } = t0;
  return stateId;
}
export default BarChart;
const DISPLAYED_PERCENT_DECIMALS = 2;

// A probability that only rounds to 0% or 100% would read as an impossible or
// certain state; the "~" keeps it distinguishable from a true 0 or 1.
export function formatStateProbability(probability: number): string {
  const rounded = Number((probability * 100).toFixed(DISPLAYED_PERCENT_DECIMALS));
  if (rounded === 0 && probability !== 0) return '~0%';
  if (rounded === 100 && probability !== 1) return '~100%';
  return `${rounded}%`;
}
function generateTitle(stateName: string | undefined, intervals: Interval[] | undefined, stateIndex: number): string | undefined {
  return `${stateName}${intervals ? ' '.concat(intervalToString(intervals[stateIndex])) : ''}`;
}